import actionCreatorFactory from 'typescript-fsa'
import { LOCATION_CHANGE } from 'react-router-redux'
import { Location } from 'history'

import { AnyEventUpdate, MapObjectEvent } from '../io/events'
import { Comment as CommentPayload } from '../states/CommentState'
import { Config, MapObject, Player } from '../states/WorldState'
import { Credentials, User } from '../states/LoginSessionState'
import { LoginResponse } from '../io/api-client/payloads'
import { Point, Rect } from '../models/BasicTypes'

const actionCreator = actionCreatorFactory()

/**
 * Actions app-wise actions
 */
export namespace App {
  export const prefix = 'APP/'

  /// Action to be dispatched after app launched
  export const launched = actionCreator<{ mobileVersion?: string }>(
    prefix + 'LAUNCHED'
  )
}

export namespace Login {
  const prefix = 'LOGIN/'

  /// Load credentials from storage
  export const loadCredentials = actionCreator.async<void, Credentials | null>(
    prefix + 'LOAD_CREDENTIALS'
  )

  /// Save credentials
  export const saveCredentials = actionCreator.async<Credentials, void>(
    prefix + 'SAVE_CREDENTIALS'
  )

  /// Notify app that the credentials is updated
  export const updateCredentials = actionCreator<Credentials | null>(
    prefix + 'UPDATE_CREDENTIALS'
  )

  /// Action for loading user info from server
  export const loadUser = actionCreator.async<void, User | null>(
    prefix + 'LOAD_USER'
  )

  /// Login button click
  export const loginClick = actionCreator(prefix + 'LOGIN_CLICK')

  /// Sign modal close
  export const loginClose = actionCreator(prefix + 'LOGIN_CLOSE')

  /// Sign up button click
  export const signUpClick = actionCreator(prefix + 'SIGN_UP_CLICK')

  /// Sign up modal close
  export const signUpClose = actionCreator(prefix + 'SIGN_UP_CLOSE')

  /// Login user
  export const login = actionCreator.async<
    { usernameOrEmail: string; password: string },
    LoginResponse,
    { [index: string]: Array<string> }
  >(prefix + 'LOGIN')

  /// Sign up user
  export const signUp = actionCreator.async<
    { username: string; email: string; password: string },
    User,
    { [index: string]: Array<string> }
  >(prefix + 'SIGN_UP')

  /// Sign up user as a guest
  export const signUpGuest = actionCreator.async<
    void,
    { user: User; credentials: Credentials }
  >(prefix + 'SIGN_UP_GUEST')
}

export namespace EventHub {
  const prefix = 'EVENT_HUB/'

  /// Subscribe a channel from event hub
  export const subscribe = actionCreator<{ channel: string; tag: string }>(
    prefix + 'SUBSCRIBE'
  )

  /// Unsubscribe a channel from event hub
  export const unsubscribe = actionCreator<string>(prefix + 'UNSUBSCRIBE')

  /// Event update
  export const eventUpdate = actionCreator<AnyEventUpdate>(
    prefix + 'EVENT_UPDATE'
  )
}

export namespace World {
  const prefix = 'WORLD/'

  /// Action for loading client settings
  export const loadConfig = actionCreator.async<void, Config>(
    prefix + 'LOAD_CONFIG'
  )

  /// Join the world as a player at given position
  export const join = actionCreator.async<Point, Player>(prefix + 'JOIN')

  /// Session created
  export const sessionCreated = actionCreator(prefix + 'SESSION_CREATED')

  /// View port is updated
  export const viewPortUpdated = actionCreator<Rect>(
    prefix + 'VIEW_PORT_UPDATED'
  )

  export const speakMessageReceived = actionCreator<{
    eventID: string
    userID: string
    userName: string
    message: string
    createdAt: number
  }>(prefix + 'SPEAK_MESSAGE_RECEIVED')

  /// Update blocks
  export const updateBlock = actionCreator<{
    add: Array<Point>
    remove: Array<Point>
  }>(prefix + 'UPDATE_BLOCK')

  /// Add map object
  export const addMapObject = actionCreator<MapObject>(
    prefix + 'ADD_MAP_OBJECT'
  )

  /// Remove map object
  export const removeMapObject = actionCreator<string>(
    prefix + 'REMOVE_MAP_OBJECT'
  )

  /// Load map object data
  export const loadMapObject = actionCreator.async<string, MapObject>(
    prefix + 'LOAD_MAP_OBJECT'
  )

  /// Map object event processed by world component
  export const eventProcessed = actionCreator<string>(
    prefix + 'EVENT_PROCESSED'
  )

  /// User trying to move to given position
  export const move = actionCreator<{
    dest: Point
    focusMapObjectPosition?: Point
  }>(prefix + 'MOVE')

  export const postMessage = actionCreator<string>(prefix + 'POST_MESSAGE')

  /// Update with local event
  export const localEventUpdate = actionCreator<MapObjectEvent>(
    prefix + 'LOCAL_EVENT_UPDATE'
  )

  /// Map object pressed
  export const mapObjectPressed = actionCreator<string>(
    prefix + 'MAP_OBJECT_PRESSED'
  )
}

export namespace MediaInput {
  const prefix = 'MEDIA_INPUT/'

  /// Select a photo
  export const selectPhoto = actionCreator<string>(prefix + 'SELECT_PHOTO')

  /// Clear the photo
  export const clearPhoto = actionCreator(prefix + 'CLEAR_PHOTO')

  /// Submit the photo
  export const submitPhoto = actionCreator<{ photo: string; note: string }>(
    prefix + 'SUBMIT_PHOTO'
  )

  /// Upload photo
  export const uploadPhoto = actionCreator.async<
    { photo: string; note: string },
    {}
  >(prefix + 'UPLOAD_PHOTO')
}

export namespace Photo {
  const prefix = 'PHOTO/'

  /// Close photo modal
  export const closeModal = actionCreator(prefix + 'CLOSE_MODAL')

  /// Photo love button tapped
  export const loveTapped = actionCreator(prefix + 'LOVE_TAPPED')

  /// Update photo
  export const updatePhoto = actionCreator.async<
    { id: string; loved: boolean },
    void
  >(prefix + 'UPDATE_PHOTO')
}

export namespace Comment {
  const prefix = 'COMMENT/'

  export const submitPhotoComment = actionCreator<{
    id: string
    content: string
  }>(prefix + 'SUBMIT_PHOTO_COMMENT')

  export const postPhotoComment = actionCreator.async<
    { id: string; content: string },
    CommentPayload
  >(prefix + 'POST_PHOTO_COMMENT')

  /// Load comments for photo
  export const loadPhotoComments = actionCreator.async<
    string,
    Array<CommentPayload>
  >(prefix + 'LOAD_PHOTO_COMMENTS')
}

export namespace Toolbar {
  const prefix = 'TOOLBAR/'

  /// Toggle message dialogue
  export const toggleDialogue = actionCreator(prefix + 'TOGGLE_DIALOGUE')

  /// Show information modal
  export const showInfoModal = actionCreator(prefix + 'SHOW_INFO_MODAL')

  /// Close information modal
  export const closeInfoModal = actionCreator(prefix + 'CLOSE_INFO_MODAL')

  /// Show sign up promote modal
  export const showSignUpPromoteModal = actionCreator(
    prefix + 'SHOW_SIGN_UP_PROMOTE_MODAL'
  )

  /// Close sign up promote modal
  export const closeSignUpPromoteModal = actionCreator(
    prefix + 'CLOSE_SIGN_UP_PROMOTE_MODAL'
  )

  /// Sign up promote tapped
  export const signUpPromoteTapped = actionCreator(
    prefix + 'SIGN_UP_PROMOTE_TAPPED'
  )
}

export namespace Router {
  export const locationChange = actionCreator<Location>(LOCATION_CHANGE)
}



// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/actions/index.ts